import {
  createContext,
  ReactNode,
  useState,
  useCallback,
  useContext,
  useRef,
  useEffect,
  useMemo,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';
import { invalidateAssetQueries } from '@/lib/assetQueryKeys';
import { createUploadBatchCoordinator } from './upload/uploadBatchCoordinator';
import { runFileUpload } from './upload/runFileUpload';
import { computeFileCrc32 } from '@/lib/crc32';
import {
  type UploadContextType,
  type UploadInfo,
  type UploadParams,
} from './upload/uploadTypes';

export type {
  UploadContextType,
  UploadInfo,
  UploadParams,
} from './upload/uploadTypes';

const DEFAULT_RESUMABLE_CHUNK_SIZE = 8 * 1024 * 1024;
const MAX_UPLOAD_ATTEMPTS = 5;
const MIN_RESUMABLE_CHUNK_SIZE = 256 * 1024;

export const useUploadContext = () => {
  const context = useContext(UploadContext);
  if (!context) {
    throw new Error(
      'useUploadContext must be used within an UploadProvider',
    );
  }
  return context;
};

const UploadContext = createContext<UploadContextType | undefined>(
  undefined,
);

export const UploadContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const queryClient = useQueryClient();
  const [managerOpen, setManagerOpen] = useState(false);
  const [list, setList] = useState<UploadInfo[]>([]);
  const abortControllersRef = useRef<Map<string, AbortController>>(
    new Map(),
  );
  const unfinishedUploadsLength = list.filter(
    (u) => u.status !== 'completed' && u.status !== 'failed',
  ).length;

  const updateUploadById = useCallback(
    (uploadId: string, patch: Partial<UploadInfo>) => {
      setList((prev) =>
        prev.map((upload) =>
          upload.id === uploadId ? { ...upload, ...patch } : upload,
        ),
      );
    },
    [],
  );

  const onUploadFinalized = useCallback(
    (domain: UploadParams['assetDomain']) =>
      invalidateAssetQueries(queryClient, domain),
    [queryClient],
  );

  const abortUploads = useCallback((uploadIds: string[]) => {
    uploadIds.forEach((uploadId) => {
      const controller = abortControllersRef.current.get(uploadId);
      if (controller) {
        controller.abort();
        abortControllersRef.current.delete(uploadId);
      }
    });
  }, []);

  const batchCoordinator = useMemo(
    () =>
      createUploadBatchCoordinator(
        updateUploadById,
        onUploadFinalized,
        abortUploads,
      ),
    [updateUploadById, onUploadFinalized, abortUploads],
  );

  const addUpload = useCallback(
    (params: UploadParams) => {
      const id = `upload-${uuidv4()}`;
      const {
        file,
        upload_url,
        file_id,
        session_error,
        onComplete,
        chunkSizeBytes = DEFAULT_RESUMABLE_CHUNK_SIZE,
        batchId,
        batchFileIds,
        assetDomain,
      } = params;

      const newUpload: UploadInfo = {
        id,
        title: file.name,
        message: 'Uploading...',
        status: 'pending',
        batchId,
      };

      setManagerOpen(true);
      setList((prev) => [...prev, newUpload]);

      if (batchId && batchFileIds?.length) {
        batchCoordinator.registerUpload(
          batchId,
          batchFileIds,
          id,
          assetDomain,
        );
      }

      if (session_error || !upload_url) {
        const message = session_error || 'Upload URL missing';
        updateUploadById(id, { status: 'failed', message });
        void batchCoordinator.markFileFailed(
          batchId,
          file_id,
          file.name,
        );
        onComplete?.(false, new Error(message));
        return id;
      }

      const abortController = new AbortController();
      abortControllersRef.current.set(id, abortController);

      // Runs alongside the upload and never fails it: a file finalized
      // without a CRC-32 is still saved, it just can't be part of a
      // server-built zip download. Stopped once the upload settles.
      const crc32Controller = new AbortController();
      const crc32Promise = computeFileCrc32(
        file,
        crc32Controller.signal,
      ).catch((error) => {
        if (!crc32Controller.signal.aborted) {
          console.warn('Could not compute CRC-32 for', file.name, error);
        }
        return undefined;
      });

      runFileUpload({
        file,
        uploadUrl: upload_url,
        fileId: file_id,
        assetDomain,
        signal: abortController.signal,
        chunkSizeBytes,
        maxAttempts: MAX_UPLOAD_ATTEMPTS,
        minChunkSize: MIN_RESUMABLE_CHUNK_SIZE,
        onProgress: (percent) => {
          updateUploadById(id, {
            progress: percent,
            message: `Uploading... ${percent}%`,
          });
        },
        onRetryMessage: (message) => {
          updateUploadById(id, { message });
        },
      })
        .then(async () => {
          const crc32 = await crc32Promise;
          await batchCoordinator.markFileUploaded(
            batchId,
            file_id,
            id,
            assetDomain,
            crc32,
          );
          onComplete?.(true);
        })
        .catch(async (error) => {
          if (!abortController.signal.aborted) {
            updateUploadById(id, {
              status: 'failed',
              progress: undefined,
              message: error?.message || 'Upload failed',
            });
            // Replaces the per-file message with the batch one and releases the reservations.
            await batchCoordinator.markFileFailed(
              batchId,
              file_id,
              file.name,
            );
            onComplete?.(false, error);
          }
        })
        .finally(() => {
          crc32Controller.abort();
          abortControllersRef.current.delete(id);
        });

      return id;
    },
    [batchCoordinator, updateUploadById],
  );

  const removeUpload = useCallback((id: string) => {
    setList((prev) => prev.filter((upload) => upload.id !== id));
  }, []);

  const cancelUpload = useCallback(
    (id: string) => {
      const abortController = abortControllersRef.current.get(id);
      if (abortController) {
        abortController.abort();
        abortControllersRef.current.delete(id);
      }

      // Cancelling one file of an all-or-nothing batch means none of them will be saved, so the
      // whole batch is released and its siblings are told why.
      const cancelled = list.find((upload) => upload.id === id);
      if (cancelled?.batchId) {
        void batchCoordinator.markFileFailed(
          cancelled.batchId,
          undefined,
          cancelled.title,
        );
      }

      setList((prev) => prev.filter((upload) => upload.id !== id));
    },
    [batchCoordinator, list],
  );

  const cancelAllUploads = useCallback(() => {
    abortControllersRef.current.forEach((abortController) => {
      abortController.abort();
    });
    abortControllersRef.current.clear();
    void batchCoordinator.releaseAll();
    setList([]);
  }, [batchCoordinator]);

  useEffect(() => {
    const beforeUnloadHandler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = true;
    };
    if (unfinishedUploadsLength > 0) {
      window.addEventListener('beforeunload', beforeUnloadHandler);
    }

    return () => {
      window.removeEventListener('beforeunload', beforeUnloadHandler);
    };
  }, [unfinishedUploadsLength]);

  return (
    <UploadContext.Provider
      value={{
        managerOpen,
        setManagerOpen,
        list,
        addUpload,
        removeUpload,
        cancelUpload,
        cancelAllUploads,
      }}
    >
      {children}
    </UploadContext.Provider>
  );
};

export { UploadContext };
export default UploadContextProvider;
